import React, { ChangeEvent, Dispatch, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { parse } from 'query-string';
import { isEmpty, filter, omit, flatten } from 'lodash-es';
import dayjs from 'dayjs';

import { 
    DateDropdown,
    SearchInput,
    SimplePopover,
    palette,
    FiltersPopover,
    FilterIcon
} from '@dev/ee-components';

import { UnpickAllCampaigns, GetCampaigns, getCampaignsStore } from '@/store/campaigns/actions';
import { CampaignsInterface } from '@/store/campaigns/types';
import { LONG_DATE_FORMAT } from '@/services/constants';
import { debounce } from '@/services/tools/debounce';
import useMobile from '@/services/hooks/useMobile';

import { getMobileFilterTabs } from '../../helpers';
import {
    getDateRangeData,
    screenCampaignTypes,
    getDateRangeValues,
    getDefaultDateRangeValue,
    DateDropdownOptions,
    getCampaignTypeOptions
} from '../../config';

import {
    MobileHeader,
    MobileSearchContainer,
    MobileFiltersContainer,
    SearchInputWrapper,
    TableFilters,
    FiltersWrapper
} from './styles';

interface MobileFiltersApplyProps {
    dispatch: Dispatch<any>;
    filtersObj: Record<string, any>;
    fromDate: string;
    toDate: string;
    campaigns: CampaignsInterface;
}

const mobileFiltersApply = ({ dispatch, filtersObj, fromDate, toDate, campaigns }: MobileFiltersApplyProps): void => {
    const dateValue = filtersObj[0] as DateDropdownOptions;
    const statusValue = filtersObj[1];
    const typesValue = filtersObj[2];
    let from;
    let to;

    let filteredStatusValue = null;
    let filteredTypesValue = null;
    const { status, perPage } = campaigns.pagination;
    const rest = omit(campaigns.pagination, ['status', 'activePage', 'perPage']);

    if (dateValue === DateDropdownOptions.Calendar) {
        from = dayjs(fromDate).startOf('day').format();
        to = dayjs(toDate).endOf('day').format();
    } else {
        const { from: fromDate, to: toDate } = getDateRangeValues(dateValue);
        from = fromDate;
        to = toDate;
    }

    if (statusValue) {
        filteredStatusValue = filter(statusValue, (el): boolean => el !== '');
    }

    if (typesValue) {
        filteredTypesValue = filter(typesValue, (el): boolean => el !== '');
    }

    dispatch(
        GetCampaigns({
            ...rest,
            type: filteredTypesValue || screenCampaignTypes,
            status: filteredStatusValue ? filteredStatusValue : status,
            limit: perPage,
            from,
            to,
        })
    );
};

const CampaignsListHeader = (): JSX.Element => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const campaigns = useSelector(getCampaignsStore);
    const query = parse(location.search);
    const startDate = query?.from?.toString();
    const endDate = query?.to?.toString();
    const defaultValue = (query.search as string) ?? '';
    const urlTypes: string[] = query.type ? flatten([query.type]) : [];

    const [selectedCampaignType, setSelectedCampaignType] = useState(urlTypes);

    const changeSearchInputVal = (event?: ChangeEvent<HTMLInputElement>): void => {
        const search = event ? event.currentTarget.value : '';
        const { type, perPage } = campaigns.pagination;
        const rest = omit(campaigns.pagination, ['activePage', 'perPage']);

        debounce((): void => {
            dispatch(UnpickAllCampaigns());
            dispatch(GetCampaigns({ ...rest, type: type || screenCampaignTypes,limit: perPage, page: 1, search }));
        }, 500);
    };

    const defaultDateRange = useMemo((): DateDropdownOptions => {
        const { from, to } = query;
        const fromDate = from && dayjs(from.toString()).format(LONG_DATE_FORMAT);
        const toDate = to && dayjs(to.toString()).format(LONG_DATE_FORMAT);
        return getDefaultDateRangeValue(fromDate, toDate);
    }, [query]);

    const setDatesBasedOnCalendar = (fromTime: Date, toTime: Date): void => {
        const from = dayjs(fromTime).startOf('day').format();
        const to = dayjs(toTime).endOf('day').format();

        const { type, perPage } = campaigns.pagination;
        const rest = omit(campaigns.pagination, ['activePage', 'perPage']);
        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns({ ...rest, type: type || screenCampaignTypes, limit: perPage, page: 1, from, to }));
    };

    const dateFilterClick = (e, obj): void => {
        const dateOptionValue = obj.value as DateDropdownOptions;
        const { from, to } = getDateRangeValues(dateOptionValue);
        const { type, perPage } = campaigns.pagination;
        const rest = omit(campaigns.pagination, ['activePage', 'perPage']);

        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns({ ...rest, type: type || screenCampaignTypes, limit: perPage, page: 1, from, to }));
    };

    const onChangeTypeSelection = (_ev, selected) => {
        const { perPage } = campaigns.pagination;
        const rest = omit(campaigns.pagination, ['activePage', 'perPage']);
        setSelectedCampaignType(selected);

        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns({ ...rest, type: isEmpty(selected) ? screenCampaignTypes : selected, limit: perPage, page: 1 }));
        
    };

    const onMobileFiltersApply = (filtersObj, fromDate, toDate): void => {
        mobileFiltersApply({
            dispatch,
            filtersObj,
            fromDate,
            toDate,
            campaigns,
        });
    };

    if (isMobile) {
        return (
            <MobileHeader>
                <MobileSearchContainer>
                    <SearchInput
                        id="mobileCampaignsSearchInput"
                        name="searchInput"
                        defaultValue={defaultValue}
                        onChange={changeSearchInputVal}
                        onDelete={changeSearchInputVal}
                        placeholder={t('searchforcampaigns')}
                        type="text"
                    />
                </MobileSearchContainer>
                <MobileFiltersContainer>
                    <FiltersPopover
                        onApply={onMobileFiltersApply}
                        tabs={getMobileFilterTabs(
                            campaigns,
                            defaultDateRange,
                            query?.status ? flatten([query?.status]) : [],
                            urlTypes,
                            startDate,
                            endDate,
                        )}
                        title={t('filters')}
                        translations={{
                            apply: t('apply'),
                            close: t('close'),
                        }}
                    >
                        <FilterIcon color={palette.gray[6]} width={20} height={20} />
                    </FiltersPopover>
                </MobileFiltersContainer>
            </MobileHeader>
        );
    }

    return (
        <TableFilters>
            <FiltersWrapper>
                <DateDropdown
                    options={getDateRangeData(false)}
                    defaultPlaceholder={t('all')}
                    startDate={query?.from?.toString()}
                    endDate={query?.to?.toString()}
                    value={defaultDateRange}
                    type="input"
                    returnRawData
                    calendarOnChange={setDatesBasedOnCalendar}
                    onItemClick={dateFilterClick}
                    buttonVariant="tertiary"
                    placement="top-start"
                />
                <SimplePopover
                    type="noborder"
                    options={getCampaignTypeOptions()}
                    defaultPlaceholder={t('type')}
                    id="campaignType"
                    checkboxValue={selectedCampaignType}
                    enableCheckbox
                    onCheckboxClick={onChangeTypeSelection}
                    popperProps={{
                        disablePortal: false,
                        popperOptions: {
                            fixedPosition: false,
                        },
                    }}
                    disableSentenceCase
                />
            </FiltersWrapper>
            <SearchInputWrapper>
                <SearchInput
                    id="searchCampaigns"
                    name="search"
                    placeholder={t('searchforcampaigns')}
                    type="text"
                    onChange={changeSearchInputVal}
                    onDelete={changeSearchInputVal}
                    defaultValue={defaultValue}
                />
            </SearchInputWrapper>
        </TableFilters>
    );
};

export default CampaignsListHeader;
